import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const baseurl =
    process.env.REACT_APP_BASE_URL ||
    "http://localhost:8000/api";

const handleError = (error) => {
    return error?.response?.data || { detail: error?.message || "Request failed." };
};

const getHeaders = (token) => ({
    "Content-Type": "application/json",
    "ngrok-skip-browser-warning": "true",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
});

const getToken = (getState) =>
    getState?.().auth?.access_token || localStorage.getItem("access_token");

const cleanPayload = (payload = {}) =>
    Object.fromEntries(Object.entries(payload).filter(([, value]) => value !== undefined && value !== ""));

export const getStudyPlan = createAsyncThunk(
    "studyPlanner/getStudyPlan",
    async (_, { rejectWithValue, getState }) => {
        try {
            const token = getToken(getState);
            const response = await axios.get(`${baseurl}/study-planner/plan/`, {
                headers: getHeaders(token),
            });
            return response.data;
        } catch (error) {
            const apiError = handleError(error);
            return rejectWithValue(apiError);
        }
    }
);

export const createStudyPlan = createAsyncThunk(
    "studyPlanner/createStudyPlan",
    async (studyPlanData, { rejectWithValue, getState }) => {
        try {
            const token = getToken(getState);
            const response = await axios.post(
                `${baseurl}/study-planner/plan/`,
                cleanPayload(studyPlanData),
                { headers: getHeaders(token) }
            );
            return response.data;
        } catch (error) {
            const apiError = handleError(error);
            return rejectWithValue(apiError);
        }
    }
);

export const generateStudyPlan = createAsyncThunk(
    "studyPlanner/generateStudyPlan",
    async (generateData, { rejectWithValue, getState }) => {
        try {
            const token = getToken(getState);
            const response = await axios.post(
                `${baseurl}/study-planner/plan/generate/`,
                cleanPayload(generateData),
                { headers: getHeaders(token) }
            );
            return response.data;
        } catch (error) {
            const apiError = handleError(error);
            return rejectWithValue(apiError);
        }
    }
);

export const updateStudyPlan = createAsyncThunk(
    "studyPlanner/updateStudyPlan",
    async (studyPlanData, { rejectWithValue, getState }) => {
        try {
            const token = getToken(getState);
            const response = await axios.patch(
                `${baseurl}/study-planner/plan/`,
                studyPlanData,
                { headers: getHeaders(token) }
            );
            return response.data;
        } catch (error) {
            const apiError = handleError(error);
            return rejectWithValue(apiError);
        }
    }
);

export const listStudySessions = createAsyncThunk(
    "studyPlanner/listStudySessions",
    async (_, { rejectWithValue, getState }) => {
        try {
            const token = getToken(getState);
            const response = await axios.get(`${baseurl}/study-planner/sessions/`, {
                headers: getHeaders(token),
            });
                        return response.data;
        } catch (error) {
            const apiError = handleError(error);
                        return rejectWithValue(apiError);
        }
    }
);

export const createStudySession = createAsyncThunk(
    "studyPlanner/createStudySession",
    async (studySessionData, { rejectWithValue, getState }) => {
        try {
            const token = getToken(getState);
            const response = await axios.post(
                `${baseurl}/study-planner/sessions/`,
                cleanPayload(studySessionData),
                { headers: getHeaders(token) }
            );
                        return response.data;
        } catch (error) {
            const apiError = handleError(error);
                        return rejectWithValue(apiError);
        }
    }
);

export const getPlannerStats = createAsyncThunk(
    "studyPlanner/getPlannerStats",
    async (_, { rejectWithValue, getState }) => {
        try {
            const token = getToken(getState);
            const response = await axios.get(`${baseurl}/study-planner/stats/`, {
                headers: getHeaders(token),
            });
                        return response.data;
        } catch (error) {
            const apiError = handleError(error);
                        return rejectWithValue(apiError);
        }
    }
);

export const listCalendarEvents = createAsyncThunk(
    "studyPlanner/listCalendarEvents",
    async (_, { rejectWithValue, getState }) => {
        try {
            const token = getToken(getState);
            const response = await axios.get(`${baseurl}/study-planner/calendar/`, {
                headers: getHeaders(token),
            });
                        return response.data;
        } catch (error) {
            const apiError = handleError(error);
                        return rejectWithValue(apiError);
        }
    }
);

export const createCalendarEvent = createAsyncThunk(
    "studyPlanner/createCalendarEvent",
    async (calendarEventData, { rejectWithValue, getState }) => {
        try {
            const token = getToken(getState);
            const response = await axios.post(
                `${baseurl}/study-planner/calendar/`,
                cleanPayload(calendarEventData),
                { headers: getHeaders(token) }
            );
                        return response.data;
        } catch (error) {
            const apiError = handleError(error);
                        return rejectWithValue(apiError);
        }
    }
);

export const updateCalendarEvent = createAsyncThunk(
    "studyPlanner/updateCalendarEvent",
    async ({ eventId, calendarEventData }, { rejectWithValue, getState }) => {
        try {
            if (!eventId) {
                return rejectWithValue({ detail: "Calendar event id is required." });
            }
            const token = getToken(getState);
            const response = await axios.patch(
                `${baseurl}/study-planner/calendar/${eventId}/`,
                cleanPayload(calendarEventData),
                { headers: getHeaders(token) }
            );
                        return response.data;
        } catch (error) {
            const apiError = handleError(error);
                        return rejectWithValue(apiError);
        }
    }
);

export const deleteCalendarEvent = createAsyncThunk(
    "studyPlanner/deleteCalendarEvent",
    async ({ eventId }, { rejectWithValue, getState }) => {
        try {
            if (!eventId) {
                return rejectWithValue({ detail: "Calendar event id is required." });
            }
            const token = getToken(getState);
            await axios.delete(`${baseurl}/study-planner/calendar/${eventId}/`, {
                headers: getHeaders(token),
            });
                        return { eventId };
        } catch (error) {
            const apiError = handleError(error);
                        return rejectWithValue(apiError);
        }
    }
);
